import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMembres, createMembre, updateMembre, deleteMembre, getParametrage, resetMemberPassword } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch, FiX, FiDownload, FiKey } from 'react-icons/fi';

const formatMoney = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

const Membres = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, hasModuleWriteAccess } = useAuth();
  const canWrite = hasModuleWriteAccess('membres');
  const [membres, setMembres] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterSexe, setFilterSexe] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [directions, setDirections] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetResult, setResetResult] = useState<any>(null);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState({
    prenom: '', nom: '', matricule: '', email: '', telephone: '',
    direction_id: '', direction: '', service: '', sexe: '', role: 'membre',
    cni: '', typepiece: '', datenaissance: '', lieunaissance: '',
    corps: '', hierarchie: '', adresse: ''
  });

  const loadData = () => {
    getMembres({ page, limit, q: search, direction_id: filterDirection, role: filterRole, sexe: filterSexe }).then(r => {
      setMembres(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.totalPages);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, [page, search, filterDirection, filterRole, filterSexe, limit]);
  useEffect(() => {
    getParametrage('directions').then(r => setDirections(r.data)).catch(() => {});
  }, []);

  const resetForm = () => setForm({
    prenom: '', nom: '', matricule: '', email: '', telephone: '',
    direction_id: '', direction: '', service: '', sexe: '', role: 'membre',
    cni: '', typepiece: '', datenaissance: '', lieunaissance: '',
    corps: '', hierarchie: '', adresse: ''
  });
  const resetFilters = () => { setFilterDirection(''); setFilterRole(''); setFilterSexe(''); setSearch(''); setPage(1); };

  const activeFiltersCount = [filterDirection, filterRole, filterSexe].filter(Boolean).length;

  const openAdd = () => { resetForm(); setEditingId(''); setShowModal(true); };
  const openEdit = (m: any) => {
    setForm({
      prenom: m.prenom || '', nom: m.nom || '', matricule: m.matricule || '',
      email: m.email || '', telephone: m.telephone || '',
      direction_id: m.direction_id || '', direction: m.direction || '',
      service: m.service || '', sexe: m.sexe || '', role: m.role || 'membre',
      cni: m.cni || '', typepiece: m.typepiece || '',
      datenaissance: m.datenaissance || '', lieunaissance: m.lieunaissance || '',
      corps: m.corps || '', hierarchie: m.hierarchie || '', adresse: m.adresse || ''
    });
    setEditingId(m.id);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateMembre(editingId, form);
        setSuccess('Membre mis à jour');
      } else {
        await createMembre(form);
        setSuccess('Membre créé avec succès');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Désactiver ce membre ?')) return;
    await deleteMembre(id);
    loadData();
  };

  const handleResetPassword = async (member: any) => {
    const name = member.prenomnom || `${member.prenom} ${member.nom}`;
    if (!window.confirm(`Réinitialiser le mot de passe de ${name} ?\n\nUn nouveau mot de passe temporaire sera généré${member.email ? ' et envoyé par email.' : '.'}`)) return;
    setResetting(true);
    try {
      const r = await resetMemberPassword(member.id);
      setResetResult({ ...r.data, member_name: name, member_email: member.email });
      setShowResetModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la réinitialisation');
      setTimeout(() => setError(''), 5000);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Liste des membres</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <span>Membres enregistrés ({total.toLocaleString('fr-FR')})</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {canWrite && <button className="btn btn-success" onClick={openAdd}><FiPlus /> Nouvel utilisateur</button>}
          </div>
        </div>
        <div className="card-body">
          {/* Search + Filters on same line */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
            <div className="search-box" style={{ flex: 1, minWidth: 180 }}>
              <FiSearch className="search-icon" />
              <input type="text" className="form-input" placeholder="Rechercher nom, matricule, email, tél..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-input" style={{ width: 'auto', minWidth: 160 }} value={filterDirection} onChange={e => { setFilterDirection(e.target.value); setPage(1); }}>
              <option value="">Direction ({directions.length})</option>
              {directions.map((d: any) => <option key={d.id} value={d.id}>{d.libelle}</option>)}
            </select>
            <select className="form-input" style={{ width: 'auto', minWidth: 120 }} value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }}>
              <option value="">Rôle</option>
              <option value="membre">Membre</option>
              <option value="bureau">Bureau</option>
              <option value="admin">Admin</option>
              <option value="admin_direction">Admin Dir.</option>
              <option value="superadmin">Super Admin</option>
            </select>
            <select className="form-input" style={{ width: 'auto', minWidth: 90 }} value={filterSexe} onChange={e => { setFilterSexe(e.target.value); setPage(1); }}>
              <option value="">Sexe</option>
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
            <select className="form-input" style={{ width: 'auto', minWidth: 80 }} value={limit} onChange={e => { setLimit(parseInt(e.target.value)); setPage(1); }}>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
            {activeFiltersCount > 0 && (
              <button className="btn btn-outline" onClick={resetFilters} style={{ color: '#f44336', whiteSpace: 'nowrap' }}>
                <FiX /> Reset
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Matricule</th>
                  <th>Prénom Nom</th>
                  <th>Téléphone</th>
                  <th>Direction</th>
                  <th>Service</th>
                  <th>Sexe</th>
                  <th>Rôle</th>
                  <th style={{ textAlign: 'right' }}>Total versé</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {membres.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>Aucun membre trouvé</td></tr>
                )}
                {membres.map(m => (
                  <tr key={m.id}>
                    <td><strong>{m.matricule || '-'}</strong></td>
                    <td><span className="link" onClick={() => navigate(`/admin/membres/${m.id}`)}>{m.prenomnom}</span></td>
                    <td>{m.telephone || '-'}</td>
                    <td>{m.direction || '-'}</td>
                    <td>{m.service || '-'}</td>
                    <td>{m.sexe || '-'}</td>
                    <td>
                      <span className={`badge ${m.role === 'superadmin' ? 'badge-red' : m.role === 'admin_direction' ? 'badge-green' : 'badge-blue'}`}>
                        {m.role === 'admin_direction' ? 'Admin Dir.' : m.role}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: m.total_versements > 0 ? 600 : 400 }}>
                      {m.total_versements > 0 ? formatMoney(m.total_versements) : '0'}
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon btn-info" onClick={() => navigate(`/admin/membres/${m.id}`)}><FiEye /></button>
                        {canWrite && <button className="btn-icon btn-warning" onClick={() => openEdit(m)}><FiEdit2 /></button>}
                        {canWrite && <button className="btn-icon" style={{ color: '#FF9800' }} onClick={() => handleResetPassword(m)} title="Réinitialiser le mot de passe" disabled={resetting}><FiKey /></button>}
                        {canWrite && <button className="btn-icon btn-danger" onClick={() => handleDelete(m.id)}><FiTrash2 /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 13, color: '#666' }}>
              Page {page} / {totalPages} — {total.toLocaleString('fr-FR')} membres
            </span>
            {totalPages > 1 && (
              <div className="pagination">
                {page > 1 && <button onClick={() => setPage(1)}>1</button>}
                {page > 3 && <span style={{ padding: '0 6px' }}>...</span>}
                {page > 1 && <button onClick={() => setPage(page - 1)}>&laquo;</button>}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  if (p > totalPages || p < 1) return null;
                  return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
                })}
                {page < totalPages && <button onClick={() => setPage(page + 1)}>&raquo;</button>}
                {page < totalPages - 2 && <span style={{ padding: '0 6px' }}>...</span>}
                {page < totalPages - 1 && <button onClick={() => setPage(totalPages)}>{totalPages}</button>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reset Password Result Modal */}
      {showResetModal && resetResult && (
        <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Mot de passe réinitialisé</h3>
              <button className="modal-close" onClick={() => setShowResetModal(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: resetResult.email_sent ? '#E8F5E9' : '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <FiKey size={28} color={resetResult.email_sent ? '#4CAF50' : '#FF9800'} />
              </div>
              <h4 style={{ margin: '0 0 4px', color: '#333' }}>{resetResult.member_name}</h4>
              <p style={{ fontSize: '0.85rem', color: '#888', margin: '0 0 16px' }}>{resetResult.member_email || 'Pas d\'email'}</p>

              <div style={{ background: '#f8f9fa', border: '2px dashed #1565C0', borderRadius: 8, padding: 16, margin: '0 0 16px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '0.8rem', color: '#666' }}>Nouveau mot de passe temporaire :</p>
                <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#1565C0', letterSpacing: 3, fontFamily: 'monospace' }}>{resetResult.temp_password}</p>
              </div>

              {resetResult.email_sent ? (
                <div style={{ background: '#E8F5E9', borderRadius: 8, padding: '10px 16px', fontSize: '0.85rem', color: '#2E7D32' }}>
                  Le nouveau mot de passe a été envoyé par email à <strong>{resetResult.member_email}</strong>
                </div>
              ) : (
                <div style={{ background: '#FFF3E0', borderRadius: 8, padding: '10px 16px', fontSize: '0.85rem', color: '#E65100' }}>
                  Ce membre n'a pas d'adresse email. Veuillez lui communiquer ce mot de passe manuellement.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowResetModal(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifier le membre' : 'Nouvel utilisateur'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <h4 style={{ margin: '0 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Identité</h4>
                <div className="form-row">
                  <div className="form-group"><label>Prénom *</label><input className="form-input" value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} required /></div>
                  <div className="form-group"><label>Nom *</label><input className="form-input" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Sexe</label>
                    <select className="form-input" value={form.sexe} onChange={e => setForm({ ...form, sexe: e.target.value })}>
                      <option value="">--</option><option value="M">Masculin</option><option value="F">Féminin</option>
                    </select>
                  </div>
                  <div className="form-group"><label>Date de naissance</label><input className="form-input" value={form.datenaissance} onChange={e => setForm({ ...form, datenaissance: e.target.value })} placeholder="JJ/MM/AAAA" /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Lieu de naissance</label><input className="form-input" value={form.lieunaissance} onChange={e => setForm({ ...form, lieunaissance: e.target.value })} /></div>
                  <div className="form-group"><label>Adresse</label><input className="form-input" value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} /></div>
                </div>

                <h4 style={{ margin: '16px 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Pièce d'identité</h4>
                <div className="form-row">
                  <div className="form-group"><label>Type de pièce</label>
                    <select className="form-input" value={form.typepiece} onChange={e => setForm({ ...form, typepiece: e.target.value })}>
                      <option value="">--</option>
                      <option value="CNI">CNI</option>
                      <option value="Passeport">Passeport</option>
                      <option value="Permis">Permis de conduire</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  <div className="form-group"><label>N° Pièce d'identité</label><input className="form-input" value={form.cni} onChange={e => setForm({ ...form, cni: e.target.value })} /></div>
                </div>

                <h4 style={{ margin: '16px 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Contact</h4>
                <div className="form-row">
                  <div className="form-group"><label>Email</label><input type="email" className="form-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="form-group"><label>Téléphone</label><input className="form-input" value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} /></div>
                </div>

                <h4 style={{ margin: '16px 0 12px', color: '#1565C0', borderBottom: '1px solid #e0e0e0', paddingBottom: 6 }}>Informations professionnelles</h4>
                <div className="form-row">
                  <div className="form-group"><label>Matricule</label><input className="form-input" value={form.matricule} onChange={e => setForm({ ...form, matricule: e.target.value })} /></div>
                  <div className="form-group"><label>Corps</label><input className="form-input" value={form.corps} onChange={e => setForm({ ...form, corps: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Hiérarchie</label><input className="form-input" value={form.hierarchie} onChange={e => setForm({ ...form, hierarchie: e.target.value })} /></div>
                  <div className="form-group"><label>Direction</label>
                    <select className="form-input" value={form.direction_id} onChange={e => setForm({ ...form, direction_id: e.target.value })}>
                      <option value="">--</option>
                      {directions.map((d: any) => <option key={d.id} value={d.id}>{d.libelle}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Service</label><input className="form-input" value={form.service} onChange={e => setForm({ ...form, service: e.target.value })} /></div>
                  <div className="form-group"><label>Rôle</label>
                    <select className="form-input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                      <option value="membre">Membre</option>
                      {isSuperAdmin() && <option value="admin_direction">Admin Direction</option>}
                      <option value="bureau">Bureau</option>
                      <option value="admin">Admin</option>
                      {isSuperAdmin() && <option value="superadmin">Super Admin</option>}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Membres;
